import React, { useMemo, useState } from 'react';
import styles from './EvidenceSourceSelector.module.css';
import { FileData } from '../FileItem/FileItem';
import LoadingIcon from '../LoadingIcon/LoadingIcon';
import Tooltip from '../Tooltip/Tooltip';
import Popup from '../Popup/Popup';
import AddEvidencePopup, { SavedToLibraryItem } from '../AddEvidencePopup/AddEvidencePopup';
import { dedupeLibraryFileRefs, LibraryFileRef } from '../../../Types/Library/Library';
import { getFileIcon } from '../../../Helpers/fileIcon';

const VISIBLE_CHIP_LIMIT = 4;

export interface EvidenceSourceSelectorProps {
  files: FileData[];
  loading: boolean;
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  /**
   * When set, X persists the removal (unlink library FileLink or delete an
   * inline upload) instead of only dropping the id from local selection.
   */
  onRemoveEvidence?: (file: FileData) => void | Promise<void>;
  removingFileId?: string | null;
  canLinkEvidence: boolean;
  roleName: string;
  title?: string;
  hint?: string;
  /** Visual treatment. `"mia"` matches Analyse AI chat chrome. */
  variant?: 'default' | 'mia';
  /** When true, header can expand/collapse the body. */
  collapsible?: boolean;
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  /** Persistent warning shown in the tile (e.g. insufficient evidence). */
  alertMessage?: string | null;
  /** When provided, enables the Add evidence popup for linking library files. */
  addEvidenceContext?: {
    areaId: string;
    questionId: string;
    linkType?: string;
    clientId: string;
    userSub: string;
    contextLabel?: string;
    onEvidenceAdded?: (linkedRefs: LibraryFileRef[]) => void;
    onUploadedFilesSelected?: (fileIds: string[]) => void;
    onUploadedFilesChanged?: () => void;
    onSavedToLibrary?: (saved: SavedToLibraryItem[]) => void;
    onUploadConversationFile?: (fileList: FileList | null) => Promise<void>;
    uploadingConversationFile?: boolean;
    uploadsConversationId?: string;
    subjectNoun?: string;
  };
}

const DEFAULT_TITLE = 'Generate summary from evidence';
const DEFAULT_HINT =
  'Evidence you add is selected automatically. The X unlinks a library file or deletes an uploaded file.';

const EvidenceSourceSelector: React.FC<EvidenceSourceSelectorProps> = ({
  files,
  loading,
  selectedIds,
  onSelectionChange,
  onRemoveEvidence,
  removingFileId = null,
  canLinkEvidence,
  roleName,
  title = DEFAULT_TITLE,
  hint = DEFAULT_HINT,
  variant = 'default',
  collapsible = false,
  collapsed: collapsedProp,
  onCollapsedChange,
  alertMessage,
  addEvidenceContext,
}) => {
  const [showAll, setShowAll] = useState(true);
  const [addEvidenceOpen, setAddEvidenceOpen] = useState(false);
  const [internalCollapsed, setInternalCollapsed] = useState(false);

  const isControlled = collapsedProp !== undefined;
  const collapsed = isControlled ? collapsedProp : internalCollapsed;

  const setCollapsed = (next: boolean) => {
    if (!isControlled) {
      setInternalCollapsed(next);
    }
    onCollapsedChange?.(next);
  };

  const linkedFileRefs: LibraryFileRef[] = useMemo(
    () =>
      dedupeLibraryFileRefs(
        files
          .filter((file) => file.source !== 'uploaded' && file.portfolioId && file.folderId && file.fileName)
          .map((file) => ({
            portfolioId: file.portfolioId,
            folderId: file.folderId,
            fileName: file.fileName,
            fileId: file.source === 'library' ? file.id : undefined,
          }))
      ),
    [files]
  );

  const deselectFile = (fileId: string) => {
    onSelectionChange(selectedIds.filter((id) => id !== fileId));
  };

  const persistRemoves = !!onRemoveEvidence;

  const handleRemoveClick = (file: FileData) => {
    if (persistRemoves) {
      if (!canLinkEvidence || removingFileId) {
        return;
      }
      void onRemoveEvidence(file);
      return;
    }
    deselectFile(file.id);
  };

  const handleEvidenceAdded = (linkedRefs: LibraryFileRef[]) => {
    addEvidenceContext?.onEvidenceAdded?.(linkedRefs);
  };

  const handleUploadedFilesSelected = (fileIds: string[]) => {
    addEvidenceContext?.onUploadedFilesSelected?.(fileIds);
  };

  const handleUploadedFilesChanged = () => {
    addEvidenceContext?.onUploadedFilesChanged?.();
  };

  const selectedFiles = useMemo(
    () => files.filter((file) => selectedIds.includes(file.id)),
    [files, selectedIds]
  );

  const visibleSelectedFiles = useMemo(() => {
    if (showAll || selectedFiles.length <= VISIBLE_CHIP_LIMIT) {
      return selectedFiles;
    }
    return selectedFiles.slice(0, VISIBLE_CHIP_LIMIT);
  }, [selectedFiles, showAll]);

  const canOpenAddEvidence = !!addEvidenceContext && canLinkEvidence;
  const isMia = variant === 'mia';
  const showBody = !collapsible || !collapsed;

  const toggleCollapsed = () => {
    if (!collapsible) return;
    setCollapsed(!collapsed);
  };

  const openAddEvidence = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setAddEvidenceOpen(true);
  };

  return (
    <div
      className={`${styles.container} ${isMia ? styles.mia : ''} ${
        collapsed && collapsible ? styles.collapsed : ''
      }`}
    >
      <div
        className={`${styles.header} ${collapsible ? styles.headerCollapsible : ''}`}
        onClick={collapsible ? toggleCollapsed : undefined}
        role={collapsible ? 'button' : undefined}
        tabIndex={collapsible ? 0 : undefined}
        aria-expanded={collapsible ? !collapsed : undefined}
        onKeyDown={
          collapsible
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleCollapsed();
                }
              }
            : undefined
        }
      >
        <div className={styles.titleGroup}>
          <div className={styles.titleRow}>
            {isMia ? (
              <span className={styles.miaOrb} aria-hidden>
                <img src="/img/sparkle-blue.svg" alt="" className={styles.sparkleIcon} />
              </span>
            ) : (
              <img src="/img/sparkle-blue.svg" alt="" className={styles.sparkleIcon} aria-hidden />
            )}
            <h2 className={styles.title}>{title}</h2>
            {selectedFiles.length > 0 && (
              <span className={styles.countBadge}>{selectedFiles.length}</span>
            )}
          </div>
          {showBody && <p className={styles.hint}>{hint}</p>}
          {!showBody && selectedFiles.length > 0 && (
            <p className={styles.collapsedSummary}>
              {selectedFiles.length === 1
                ? '1 file selected for this chat'
                : `${selectedFiles.length} files selected for this chat`}
            </p>
          )}
          {!showBody && selectedFiles.length === 0 && (
            <p className={styles.collapsedSummary}>No evidence attached yet</p>
          )}
        </div>
        <div className={styles.headerActions} onClick={(e) => e.stopPropagation()}>
          {showBody && selectedFiles.length > VISIBLE_CHIP_LIMIT && (
            <button
              type="button"
              className={styles.showAllButton}
              onClick={() => setShowAll((prev) => !prev)}
              aria-expanded={showAll}
            >
              {showAll ? 'Show less' : 'Show all'}
              <img src="/img/down-arrow.svg" alt="" width={12} height={12} />
            </button>
          )}
          <Tooltip
            text={`Your role as a ${roleName} means that you cannot link evidence`}
            align="top"
            sideAlign="left"
            disabled={canLinkEvidence}
          >
            <button
              type="button"
              className={styles.addEvidenceButton}
              onClick={openAddEvidence}
              disabled={!canOpenAddEvidence}
            >
              + Add evidence
            </button>
          </Tooltip>
          {collapsible && (
            <button
              type="button"
              className={`${styles.collapseToggle} ${collapsed ? styles.collapseToggleCollapsed : ''}`}
              onClick={toggleCollapsed}
              aria-label={collapsed ? 'Expand evidence' : 'Collapse evidence'}
              aria-expanded={!collapsed}
            >
              <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <path d="M2 4.5L6 8.5L10 4.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div
        className={`${styles.body} ${showBody ? styles.bodyOpen : styles.bodyClosed}`}
        aria-hidden={!showBody}
      >
        <div className={styles.bodyInner}>
          {loading ? (
            <div className={styles.loadingContainer}>
              <LoadingIcon />
            </div>
          ) : files.length === 0 ? (
            <p className={styles.emptyState}>
              There are no evidence files linked yet. Use &quot;Add evidence&quot; to upload files or
              link from your library.
            </p>
          ) : selectedFiles.length === 0 ? (
            <p className={styles.emptyState}>
              No evidence selected yet. Use &quot;Add evidence&quot; to upload files or link from your
              library.
            </p>
          ) : (
            <div className={styles.chipRow}>
              {visibleSelectedFiles.map((file) => (
                  <div key={file.id} className={`${styles.chip} ${styles.chipSelected}`}>
                    <img src={getFileIcon(file.fileName)} alt="" className={styles.fileIcon} />
                    <p className={styles.fileName} title={file.fileName}>
                      {file.fileName}
                    </p>
                    {file.source === 'uploaded' && (
                      <span className={styles.uploadedBadge}>uploaded</span>
                    )}
                    <button
                      type="button"
                      className={styles.removeButton}
                      onClick={() => handleRemoveClick(file)}
                      disabled={
                        persistRemoves &&
                        (!canLinkEvidence || !!removingFileId)
                      }
                      aria-label={
                        persistRemoves
                          ? file.source === 'uploaded'
                            ? `Delete uploaded file ${file.fileName}`
                            : `Unlink ${file.fileName} from this ${addEvidenceContext?.subjectNoun ?? 'criteria'}`
                          : `Remove ${file.fileName} from summary`
                      }
                    >
                      {removingFileId === file.id ? '…' : '×'}
                    </button>
                  </div>
              ))}
            </div>
          )}
          {alertMessage && (
            <p className={styles.alert} role="status">
              {alertMessage}
            </p>
          )}
        </div>
      </div>

      {addEvidenceContext && addEvidenceOpen && (
        <Popup isVisible={addEvidenceOpen} onClose={() => setAddEvidenceOpen(false)} allowSnapToSide>
          <AddEvidencePopup
            areaId={addEvidenceContext.areaId}
            questionId={addEvidenceContext.questionId}
            linkType={addEvidenceContext.linkType}
            clientId={addEvidenceContext.clientId}
            userSub={addEvidenceContext.userSub}
            contextLabel={addEvidenceContext.contextLabel}
            linkedFileRefs={linkedFileRefs}
            onEvidenceAdded={handleEvidenceAdded}
            onUploadedFilesSelected={handleUploadedFilesSelected}
            onUploadedFilesChanged={handleUploadedFilesChanged}
            onSavedToLibrary={addEvidenceContext.onSavedToLibrary}
            onUploadConversationFile={addEvidenceContext.onUploadConversationFile}
            uploadingConversationFile={addEvidenceContext.uploadingConversationFile}
            uploadsConversationId={addEvidenceContext.uploadsConversationId}
            subjectNoun={addEvidenceContext.subjectNoun}
            onClose={() => setAddEvidenceOpen(false)}
          />
        </Popup>
      )}
    </div>
  );
};

export default EvidenceSourceSelector;
